import { ref } from 'vue';

/**
 * A composable function for pagination management.
 *
 * @returns An object containing:
 *   - `pagination` - Reactive pagination state object
 *   - `CallSetPagination` - Function to set pagination values
 *   - `CallPaginate` - Function to update current page and page size, returns a function to execute a callback
 *   - `CallReset` - Function to reset pagination to default values
 *
 * @example
 * ```
 * const { pagination, CallSetPagination, CallPaginate, CallReset } = usePaginator();
 *
 * // Set custom pagination values
 * CallSetPagination({ page: 2, pageSize: 20, firstItem: 20, total: 100 });
 *
 * // Handle pagination change from UI component
 * const handlePageChange = async ({ page, rows }) => {
 *   const paginate = await CallPaginate({ page, rows });
 *   await paginate(() => fetchData());
 * };
 *
 * // Reset pagination to defaults
 * CallReset();
 * ```
 */
function usePaginator() {
  const pagination = ref({ ...PAGINATION });

  /**
   * Updates the first item index in the pagination based on the current page and page size.
   * The first item is calculated as (page - 1) * pageSize, representing the zero-based index
   * of the first item displayed on the current page.
   */
  function updateFirstItem() {
    pagination.value.firstItem = (pagination.value.page - 1) * pagination.value.pageSize;
  }

  /**
   * Updates the pagination state with the provided values.
   * After setting the new pagination values, it updates the first item position.
   *
   * @param values - The pagination values to set
   */
  function setPagination(values: any) {
    pagination.value = { ...values };
    updateFirstItem();
  }

  /**
   * Updates the pagination state and returns a function that allows for asynchronous callback execution.
   * @param {Object} options - The pagination options
   * @param {number} options.page - Zero-based page index (will be converted to one-based)
   * @param {number} options.rows - Number of rows per page
   * @returns {Function} - A function that accepts and executes an asynchronous callback
   * @example
   * const paginateAndFetch = await paginate({ page: 0, rows: 10 });
   * await paginateAndFetch(fetchData);
   */
  async function paginate({ page, rows }: any) {
    pagination.value.page = page + 1;
    pagination.value.pageSize = rows;

    return async function (callback: (args?: any) => any) {
      await callback();
    };
  }

  /**
   * Updates pagination values based on the provided page information and executes a callback function.
   *
   * @param options - The pagination options
   * @param options.page - Zero-based index of the current page
   * @param options.rows - Number of rows per page
   * @param callback - Async function to execute after updating pagination values
   * @returns Promise that resolves to the result of the callback function
   */
  async function paginateAndFetch(
    { page, rows }: any,
    callback: (args?: any) => Promise<any>,
  ) {
    pagination.value.page = page + 1;
    pagination.value.pageSize = rows;

    return await callback();
  }

  /**
   * Resets the pagination state to its initial values.
   * This function sets the pagination value back to the default PAGINATION constant.
   * @returns void
   */
  function reset() {
    pagination.value = { ...PAGINATION };
  }

  return {
    pagination,
    CallSetPagination: setPagination,
    CallPaginate: paginate,
    CallResetPagination: reset,
    CallPaginateAndFetch: paginateAndFetch,
  };
}

export default usePaginator;
